/**
 * Tracer API Data Table Component
 * Collapsible table for displaying Tracer API device query results
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tabs,
  Tab,
  Chip,
  TextField,
  InputAdornment,
  TableSortLabel,
  TablePagination,
  Card,
  CardContent,
  List,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Badge,
  Menu,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TableChart as TableIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Fingerprint as DeviceIcon,
  PlayArrow as LoadIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Note as NoteIcon,
  Visibility,
  VisibilityOff,
  FilterList as FilterListIcon,
  CenterFocusStrong as ZoomExtentIcon,
  Public as CountryIcon,
  ViewColumn as ViewColumnIcon,
  PlaylistPlay as LoadAllIcon,
  PersonPinCircle as FocusIcon,
  Delete as DeleteIcon,
  DeleteSweep as ClearAllIcon,
} from '@mui/icons-material';


type SortDirection = 'asc' | 'desc';
type SortColumn = 'registration_id' | 'latitude' | 'longitude' | 'timestamp' | 'accuracy' | string;

interface GeoNeutronDevice {
  registration_id: string;
  latitude: number;
  longitude: number;
  timestamp: string;
  accuracy?: number;
  source?: string;
  // New fields from initial query
  ip_address?: string;  // IP address from signal
  forensic_flag?: string;  // Primary decoded flag (highest priority)
  all_forensic_flags?: string[];  // ALL applicable flags for this signal
  country?: string;  // Country code (e.g., "TUR", "USA")
  carrier?: string;  // Mobile carrier (e.g., "Vodafone Turkey")
  timezone?: string;  // Timezone (e.g., "Europe/Istanbul")
  browser_family?: string | null;
  browser_version?: string | null;
  os_family?: string | null;
  os_version?: string | null;
  device_family?: string | null;
  device_brand?: string | null;
  device_model?: string | null;
  advertiser_eu?: boolean | null;
  user_agent_full?: string | null;
  created_on?: string | null;
  updated_on?: string | null;
  // Legacy aggregated flags (from separate query - kept for backwards compat)
  forensic_flags?: {
    total_signals: number;
    signals_with_flags: number;
    unique_flags: string[];
    flag_counts: Record<string, number>;
    flag_percentages: Record<string, number>;
    all_flags: string[];
  };
  [key: string]: any; // Allow additional fields
}

export interface AnalysisTab {
  id: string;
  label: string;
  data: GeoNeutronDevice[];
  totalDevices: number;
  timestamp: string;
  type?: 'area' | 'pol' | 'query' | 'csv';
  circleInfo?: {
    center: [number, number];
    radius: number;
  };
  deviceId?: string; // For PoL tabs - the specific device ID
  polDataPoints?: any[]; // For PoL tabs - the actual data points for timeline
  dwellLocations?: any[]; // For PoL tabs - dwell locations for this device
  csvColumns?: string[]; // For CSV tabs - the original column headers
  areaDataPoints?: any[]; // For Area tabs - raw data points for fallback
  areaRoutes?: any[]; // For Area tabs - routes for fallback
  uploadedFiles?: string[]; // For Uploaded Data tab - list of uploaded file names
  alwaysVisible?: boolean; // If true, this tab's data is always visible on map
}

// Geographic bounds type for map viewport filtering
export interface MapBounds {
  north: number; // Max latitude
  south: number; // Min latitude
  east: number;  // Max longitude
  west: number;  // Min longitude
}

interface GeoNeutronDataTableProps {
  tabs: AnalysisTab[];
  isLoading?: boolean;
  error?: string | null;
  onDeviceClick?: (device: GeoNeutronDevice) => void;
  onZoomToDeviceExtent?: (registrationId: string, points: GeoNeutronDevice[]) => void;
  onTabClose?: (tabId: string) => void;
  onLoadDevice?: (registrationId: string) => void;
  onLoadAllDevices?: (registrationIds: string[]) => void; // Load PoL for all devices
  onSaveDevice?: (registrationId: string) => void;
  onDeviceNotes?: (registrationId: string) => void;
  onToggleDeviceVisibility?: (registrationId: string) => void;
  onFocusDevice?: (registrationId: string, allDeviceIds: string[]) => void; // Focus on single device, hide all others
  hiddenDevices?: Set<string>;
  savedDeviceIds?: Set<string>; // Set of registration IDs that are saved/favorited
  savedDeviceNames?: Map<string, string>;
  variant?: 'sidebar' | 'bottom-panel';
  activeTabId?: string;
  onTabChange?: (tabId: string) => void;
  selectedCountries?: string[];
  selectedCarriers?: string[];
  selectedTimezones?: string[];
  selectedForensicFlags?: string[];
  onCountriesChange?: (countries: string[]) => void;
  onCarriersChange?: (carriers: string[]) => void;
  onTimezonesChange?: (timezones: string[]) => void;
  onForensicFlagsChange?: (flags: string[]) => void;
  // Filter by map bounds props
  filterByMapBounds?: boolean;
  onFilterByMapBoundsChange?: (enabled: boolean) => void;
  mapBounds?: MapBounds | null;
  // Delete uploaded data callback
  onDeleteUploadedPoint?: (pointId: string) => void;
  onClearAllUploadedData?: () => void;
}

const GeoNeutronDataTable: React.FC<GeoNeutronDataTableProps> = ({
  tabs,
  isLoading = false,
  error = null,
  onDeviceClick,
  onZoomToDeviceExtent,
  onTabClose,
  onLoadDevice,
  onLoadAllDevices,
  onSaveDevice,
  onDeviceNotes,
  onToggleDeviceVisibility,
  onFocusDevice,
  hiddenDevices = new Set(),
  savedDeviceIds = new Set(),
  savedDeviceNames = new Map(),
  variant = 'bottom-panel', // Default to bottom panel for backwards compatibility
  activeTabId,
  onTabChange,
  selectedCountries = [],
  selectedCarriers = [],
  selectedTimezones = [],
  selectedForensicFlags = [],
  onCountriesChange,
  onCarriersChange,
  onTimezonesChange,
  onForensicFlagsChange,
  filterByMapBounds = false,
  onFilterByMapBoundsChange,
  mapBounds,
  onDeleteUploadedPoint,
  onClearAllUploadedData,
}) => {
  const [isExpanded, setIsExpanded] = useState(true); // Start expanded for both sidebar and bottom-panel modes
  const [internalActiveTabIndex, setInternalActiveTabIndex] = useState(0);
  
  // Resizable height state for bottom-panel variant
  const [panelHeight, setPanelHeight] = useState(500);
  const [isResizing, setIsResizing] = useState(false);

  const getDisplayLabel = (registrationId: string) => {
    const custom = savedDeviceNames.get(registrationId);
    if (custom) return custom;
    return registrationId;
  };
  
  // Trigger window resize event when panel expands/collapses to notify map to resize
  React.useEffect(() => {
    if (variant === 'bottom-panel') {
      // Give CSS time to update, then trigger resize
      const timeoutId = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isExpanded, variant]);
  
  // Handle resize drag
  React.useEffect(() => {
    if (variant !== 'bottom-panel' || !isResizing) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      const newHeight = window.innerHeight - e.clientY;
      // Constrain between 150px and 80% of window height
      const minHeight = 150;
      const maxHeight = window.innerHeight * 0.8;
      setPanelHeight(Math.min(maxHeight, Math.max(minHeight, newHeight)));
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [variant, isResizing]);
  
  // Determine active index
  const activeTabIndex = useMemo(() => {
    if (activeTabId && tabs.length > 0) {
      const idx = tabs.findIndex(t => t.id === activeTabId);
      return idx >= 0 ? idx : 0;
    }
    return internalActiveTabIndex;
  }, [activeTabId, tabs, internalActiveTabIndex]);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(variant === 'sidebar' ? 10 : 100); // Smaller page size for sidebar
  
  // Debug: Log mount/unmount to track if component is being recreated
  useEffect(() => {
    console.warn('[GeoNeutronDataTable] MOUNTED', { variant, tabCount: tabs.length });
    return () => {
      console.warn('[GeoNeutronDataTable] UNMOUNTED');
    };
  }, []);
  
  // Filter dialog state (filters themselves are managed by parent)
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  // Column configuration state
    const defaultColumnWidths: Record<string, number> = {
    source: 120,
    registration_id: 200,
    latitude: 130,
    longitude: 130,
    timestamp: 180,
    accuracy: 120,
    ip_address: 150,
    country: 100,
    carrier: 150,
    timezone: 150,
    forensic_flag: 180,
    browser_family: 140,
    os_family: 100,
    device_brand: 100,
    device_model: 120,
    advertiser_eu: 100,
    browswer_version: 100,
    os_version: 100,
    device_family: 120,
    user_agent_full: 200,
    created_on: 160,
    updated_on: 160,
  };

  const defaultColumnVisibility: Record<string, boolean> = {
    source: true,
    registration_id: true,
    latitude: true,
    longitude: true,
    timestamp: true,
    accuracy: true,
    ip_address: true,
    country: true,
    carrier: true,
    timezone: true,
    forensic_flag: true,
    browser_family: true,
    os_family: true,
    device_brand: true,
    device_model: true,
    advertiser_eu: true,
    browser_version: true,
    os_version: true,
    device_family: true,
    user_agent_full: true,
    created_on: true,
    updated_on: true,
  };

  // Load column settings from localStorage
  const loadColumnSettings = () => {
    try {
      const savedWidths = localStorage.getItem('tracer_table_column_widths');
      const savedVisibility = localStorage.getItem('tracer_table_column_visibility');
      return {
        widths: savedWidths ? { ...defaultColumnWidths, ...JSON.parse(savedWidths) } : defaultColumnWidths,
        visibility: savedVisibility ? { ...defaultColumnVisibility, ...JSON.parse(savedVisibility) } : defaultColumnVisibility,
      };
    } catch {
      return {
        widths: defaultColumnWidths,
        visibility: defaultColumnVisibility,
      };
    }
  };

  const { widths: initialWidths, visibility: initialVisibility } = loadColumnSettings();
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(initialWidths);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(initialVisibility);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);

  // Save column widths to localStorage
  const saveColumnWidths = (widths: Record<string, number>) => {
    try {
      localStorage.setItem('tracer_table_column_widths', JSON.stringify(widths));
    } catch (e) {
      console.warn('Failed to save column widths:', e);
    }
  };

  // Save column visibility to localStorage
  const saveColumnVisibility = (visibility: Record<string, boolean>) => {
    try {
      localStorage.setItem('tracer_table_column_visibility', JSON.stringify(visibility));
    } catch (e) {
      console.warn('Failed to save column visibility:', e);
    }
  };

  // Handle column resize start
  const handleResizeStart = (columnKey: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = columnWidths[columnKey] || defaultColumnWidths[columnKey] || 150;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const diff = moveEvent.clientX - startX;
      const newWidth = Math.max(50, startWidth + diff); // Minimum width of 50px
      setColumnWidths(prev => {
        const updated = { ...prev, [columnKey]: newWidth };
        saveColumnWidths(updated);
        return updated;
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Toggle column visibility
  const handleToggleColumnVisibility = (columnKey: string) => {
    setColumnVisibility(prev => {
      const updated = { ...prev, [columnKey]: !prev[columnKey] };
      saveColumnVisibility(updated);
      return updated;
    });
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    if (onTabChange && tabs[newValue]) {
      onTabChange(tabs[newValue].id);
    } else {
      setInternalActiveTabIndex(newValue);
    }
    setPage(0); // Reset to first page when changing tabs
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };


  // Get current tab data
  const currentTab = tabs[activeTabIndex];
  const data = currentTab?.data || [];

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filter handlers (call parent callbacks)
  const handleClearFilters = () => {
    onCountriesChange?.([]);
    onCarriersChange?.([]);
    onTimezonesChange?.([]);
    onForensicFlagsChange?.([]);
    setPage(0);
  };

  const handleToggleCountry = (country: string) => {
    const updated = selectedCountries.includes(country) 
      ? selectedCountries.filter(c => c !== country) 
      : [...selectedCountries, country];
    onCountriesChange?.(updated);
    setPage(0);
  };

  const handleToggleCarrier = (carrier: string) => {
    const updated = selectedCarriers.includes(carrier)
      ? selectedCarriers.filter(c => c !== carrier)
      : [...selectedCarriers, carrier];
    onCarriersChange?.(updated);
    setPage(0);
  };

  const handleToggleTimezone = (timezone: string) => {
    const updated = selectedTimezones.includes(timezone)
      ? selectedTimezones.filter(t => t !== timezone)
      : [...selectedTimezones, timezone];
    onTimezonesChange?.(updated);
    setPage(0);
  };

  const handleToggleForensicFlag = (flag: string) => {
    const updated = selectedForensicFlags.includes(flag)
      ? selectedForensicFlags.filter(f => f !== flag)
      : [...selectedForensicFlags, flag];
    onForensicFlagsChange?.(updated);
    setPage(0);
  };

  // Calculate active filter count
  const activeFilterCount = selectedCountries.length + selectedCarriers.length + selectedTimezones.length + selectedForensicFlags.length;

  // Export current tab data to CSV
  const handleExportCSV = () => {
    if (!currentTab || filteredAndSortedData.length === 0) {
      return;
    }

    let csvRows: string[] = [];
    
    // For PoL and Area tabs, export ALL data points (not just summary)
    // This ensures full Pattern of Life is exported for use in AGOL/other platforms
    // For PoL and Area tabs, export ALL data points with full column set (matches table + Last IPs)
    const fullDataPoints = currentTab.polDataPoints || currentTab.areaDataPoints;
    const isAreaOrPol = currentTab.type === 'pol' || currentTab.type === 'area';
    const exportRows = (isAreaOrPol && currentTab.data && currentTab.data.length > 0)
      ? currentTab.data
      : fullDataPoints;
    const exploreDeviceDetails = (currentTab as any).exploreDeviceDetails as Record<string, any> | undefined;

    if (exportRows && exportRows.length > 0 && isAreaOrPol) {
      const headers = [
        'Source', 'Registration ID', 'Device Type', 'Latitude', 'Longitude', 'Timestamp', 'Accuracy (m)',
        'IP Address', 'Country', 'Carrier', 'Timezone', 'Forensic Flag',
        'Browser Family', 'Browser Version', 'OS Family', 'OS Version',
        'Device Brand', 'Device Family', 'Device Model', 'Advertiser EU',
        'Created On', 'Updated On', 'User Agent Full', 'Last IPs'
      ];
      csvRows.push(headers.join(','));

      const escapeCsv = (v: any): string => {
        if (v === undefined || v === null || v === '') return '-';
        const s = String(v);
        if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
        return s;
      };

      exportRows.forEach((row: any) => {
        const ts = row.timestamp instanceof Date ? row.timestamp.toISOString() : row.timestamp;
        const lat = row.latitude ?? row.coordinates?.[0];
        const lng = row.longitude ?? row.coordinates?.[1];
        const regId = row.registration_id ?? row.studentId ?? row.deviceId ?? '';
        const regIdNorm = String(regId).replace(/^GN-/i, '');
        const advEu = row.advertiser_eu === true ? 'Yes' : row.advertiser_eu === false ? 'No' : '-';
        const forensicFlag = (row.all_forensic_flags && row.all_forensic_flags.length > 0)
          ? row.all_forensic_flags.join('; ')
          : (row.forensic_flag ?? row.metadata?.forensic_flag ?? '-');

        const lastIpsEntry = regIdNorm && exploreDeviceDetails?.[regIdNorm]?.lastSeenIPAddresses;
        const lastIpsStr = Array.isArray(lastIpsEntry) && lastIpsEntry.length > 0
          ? lastIpsEntry
              .slice(0, 50)
              .map((e: any) =>
                e.timestamp != null
                  ? `${e.ipAddress ?? '–'} (${formatTimestamp(new Date(e.timestamp).toISOString())})`
                  : (e.ipAddress ?? '–')
              )
              .join('; ')
          : '-';

        const csvRow = [
          escapeCsv(row.source || 'OSINT API'),
          escapeCsv(regId),
          escapeCsv(row.deviceType || 'phone'),
          formatCoordinate(lat),
          formatCoordinate(lng),
          `"${formatTimestamp(ts)}"`,
          row.accuracy != null ? Math.round(Number(row.accuracy)) : '-',
          escapeCsv(row.ip_address ?? row.metadata?.ip_address),
          escapeCsv(row.country ?? row.metadata?.country),
          escapeCsv(row.carrier ?? row.metadata?.carrier),
          escapeCsv(row.timezone ?? row.metadata?.timezone),
          escapeCsv(forensicFlag),
          escapeCsv(row.browser_family ?? row.metadata?.browser_family),
          escapeCsv(row.browser_version ?? row.metadata?.browser_version),
          escapeCsv(row.os_family ?? row.metadata?.os_family),
          escapeCsv(row.os_version ?? row.metadata?.os_version),
          escapeCsv(row.device_brand ?? row.metadata?.device_brand),
          escapeCsv(row.device_family ?? row.metadata?.device_family),
          escapeCsv(row.device_model ?? row.metadata?.device_model),
          advEu,
          row.created_on ? `"${formatTimestamp(row.created_on)}"` : '-',
          row.updated_on ? `"${formatTimestamp(row.updated_on)}"` : '-',
          escapeCsv(row.user_agent_full ?? row.metadata?.user_agent_full),
          escapeCsv(lastIpsStr)
        ];
        csvRows.push(csvRow.join(','));
      });

      const csvContent = csvRows.join('\n');
      
      // Create blob and download
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `${currentTab.label.replace(/\s+/g, '_')}_full_path_${timestamp}.csv`;
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    // For sidebar mode with grouped devices (when no full data available), export the summary
    if (variant === 'sidebar') {
      const headers = ['Source', 'Registration ID', 'Point Count', 'Latest Latitude', 'Latest Longitude', 'Latest Timestamp'];
      csvRows.push(headers.join(','));
      
      filteredAndSortedData.forEach(deviceGroup => {
        const row = [
          deviceGroup.source,
          `"${deviceGroup.registration_id}"`,
          deviceGroup.pointCount,
          formatCoordinate(deviceGroup.lastPoint.latitude),
          formatCoordinate(deviceGroup.lastPoint.longitude),
          `"${formatTimestamp(deviceGroup.latestTimestamp)}"`
        ];
        csvRows.push(row.join(','));
      });
      
      // Create CSV content
      const csvContent = csvRows.join('\n');
      
      // Create blob and download
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `${currentTab.label.replace(/\s+/g, '_')}_devices_summary_${timestamp}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // For bottom-panel mode, export all original data points (not grouped)
    if (currentTab.type === 'csv' && currentTab.csvColumns) {
      // Dynamic CSV export for CSV tabs
      const headers = currentTab.csvColumns;
      csvRows.push(headers.join(','));

      data.forEach(device => {
        const row = headers.map(column => {
          const value = (device as any)[column];
          // Handle special formatting
          if (column === 'latitude' || column === 'longitude') {
            return formatCoordinate(value);
          } else if (column === 'timestamp') {
            return `"${formatTimestamp(value)}"`;
          } else if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            // Escape CSV special characters
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value || '';
        });
        csvRows.push(row.join(','));
      });
    } else {
      // Standard export for non-CSV tabs (using original ungrouped data)
      const headers = ['Source', 'Registration ID', 'Latitude', 'Longitude', 'Timestamp', 'Accuracy (m)'];
      csvRows.push(headers.join(','));

      data.forEach(device => {
        const row = [
          'OSINT API',
          `"${device.registration_id}"`,
          formatCoordinate(device.latitude),
          formatCoordinate(device.longitude),
          `"${formatTimestamp(device.timestamp)}"`,
          device.accuracy ? Math.round(device.accuracy) : '-'
        ];
        csvRows.push(row.join(','));
      });
    }

    // Create CSV content
    const csvContent = csvRows.join('\n');
    
    // Create blob and download
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    // Generate filename with tab label and timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `${currentTab.label.replace(/\s+/g, '_')}_${timestamp}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleString('en-US', { timeZone: 'UTC', hour12: false }) + ' UTC';
    } catch {
      return timestamp;
    }
  };

  const formatCoordinate = (coord: number | string | undefined | null) => {
    // Handle various input types
    if (coord === undefined || coord === null) {
      return '0.000000';
    }
    
    // Convert to number if string
    const numCoord = typeof coord === 'string' ? parseFloat(coord) : coord;
    
    // Check if valid number
    if (isNaN(numCoord)) {
      return '0.000000';
    }
    
    return numCoord.toFixed(6);
  };

  // Extract unique filter options from data
  const filterOptions = useMemo(() => {
    const countries = new Set<string>();
    const carriers = new Set<string>();
    const timezones = new Set<string>();
    const forensicFlags = new Set<string>();

    data.forEach(device => {
      if (device.country) countries.add(device.country);
      if (device.carrier) carriers.add(device.carrier);
      if (device.timezone) timezones.add(device.timezone);
      // Use all_forensic_flags if available (contains ALL flags from bit decoding)
      if (device.all_forensic_flags && Array.isArray(device.all_forensic_flags)) {
        device.all_forensic_flags.forEach(flag => forensicFlags.add(flag));
      } else if (device.forensic_flag) {
        // Fallback to single flag
        forensicFlags.add(device.forensic_flag);
      }
      // Also check legacy aggregated forensic flags
      if ((device as any).forensic_flags?.unique_flags) {
        (device as any).forensic_flags.unique_flags.forEach((flag: string) => forensicFlags.add(flag));
      }
    });

    return {
      countries: Array.from(countries).sort(),
      carriers: Array.from(carriers).sort(),
      timezones: Array.from(timezones).sort(),
      forensicFlags: Array.from(forensicFlags).sort(),
    };
  }, [data]);

  // Group data by device (one entry per unique registration_id with point count)
  const groupedDevices = useMemo(() => {
    const deviceMap = new Map<string, {
      registration_id: string;
      pointCount: number;
      firstPoint: GeoNeutronDevice;
      lastPoint: GeoNeutronDevice;
      source: string;
      latestTimestamp: string;
      forensic_flags?: any; // Preserve forensic flags
      allForensicFlags: Set<string>; // Collect ALL forensic flags from ALL signals
    }>();

    data.forEach(device => {
      const regId = device.registration_id;
      if (!deviceMap.has(regId)) {
        const flagsSet = new Set<string>();
        // Collect ALL forensic flags from this signal
        if (device.all_forensic_flags && Array.isArray(device.all_forensic_flags)) {
          device.all_forensic_flags.forEach(flag => flagsSet.add(flag));
        } else if (device.forensic_flag) {
          flagsSet.add(device.forensic_flag);
        }
        // Also check legacy aggregated flags
        if ((device as any).forensic_flags?.unique_flags) {
          (device as any).forensic_flags.unique_flags.forEach((flag: string) => flagsSet.add(flag));
        }
        
        deviceMap.set(regId, {
          registration_id: regId,
          pointCount: 1,
          firstPoint: device,
          lastPoint: device,
          source: device.source || 'GeoNeutron',
          latestTimestamp: device.timestamp,
          forensic_flags: (device as any).forensic_flags, // Preserve forensic flags from device
          allForensicFlags: flagsSet
        });
      } else {
        const existing = deviceMap.get(regId)!;
        existing.pointCount++;
        
        // Collect ALL forensic flags from this signal
        if (device.all_forensic_flags && Array.isArray(device.all_forensic_flags)) {
          device.all_forensic_flags.forEach(flag => existing.allForensicFlags.add(flag));
        } else if (device.forensic_flag) {
          existing.allForensicFlags.add(device.forensic_flag);
        }
        // Also check legacy aggregated flags
        if ((device as any).forensic_flags?.unique_flags) {
          (device as any).forensic_flags.unique_flags.forEach((flag: string) => existing.allForensicFlags.add(flag));
        }
        
        // Update latest timestamp
        if (new Date(device.timestamp) > new Date(existing.latestTimestamp)) {
          existing.latestTimestamp = device.timestamp;
          existing.lastPoint = device;
        }
        // Preserve forensic flags if they exist (might be on any device entry)
        if ((device as any).forensic_flags && !existing.forensic_flags) {
          existing.forensic_flags = (device as any).forensic_flags;
        }
      }
    });

    return Array.from(deviceMap.values());
  }, [data]);

  // Helper function to check if a point is within map bounds
  const isWithinBounds = (lat: number, lng: number, bounds: MapBounds | null | undefined): boolean => {
    if (!bounds) return true;
    return lat >= bounds.south && lat <= bounds.north && lng >= bounds.west && lng <= bounds.east;
  };

  // Filter, sort, and paginate grouped devices
  const filteredAndSortedData = useMemo(() => {
    let filtered = groupedDevices;
    
    // Apply map bounds filter first (filter devices that have at least one point in view)
    if (filterByMapBounds && mapBounds) {
      filtered = filtered.filter(device => 
        isWithinBounds(device.lastPoint.latitude, device.lastPoint.longitude, mapBounds)
      );
    }
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(device => {
        const label = getDisplayLabel(device.registration_id);
        return (
          device.registration_id?.toLowerCase().includes(searchLower) ||
          label.toLowerCase().includes(searchLower) ||
          device.lastPoint.latitude?.toString().includes(searchLower) ||
          device.lastPoint.longitude?.toString().includes(searchLower)
        );
    });
    }
    
    // Apply metadata filters
    if (selectedCountries.length > 0) {
      filtered = filtered.filter(device => 
        selectedCountries.includes(device.lastPoint.country || '')
      );
    }
    
    if (selectedCarriers.length > 0) {
      filtered = filtered.filter(device => 
        selectedCarriers.includes(device.lastPoint.carrier || '')
      );
    }
    
    if (selectedTimezones.length > 0) {
      filtered = filtered.filter(device => 
        selectedTimezones.includes(device.lastPoint.timezone || '')
      );
    }
    
    if (selectedForensicFlags.length > 0) {
      filtered = filtered.filter(device => {
        // Check if ANY of the device's forensic flags match the selected filters
        // Use the allForensicFlags set which contains flags from ALL signals for this device
        return Array.from(device.allForensicFlags || []).some(flag => 
          selectedForensicFlags.includes(flag)
        );
      });
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      // Sort by point count by default, or by selected column
      if (sortColumn === 'timestamp') {
        const aVal = new Date(a.latestTimestamp).getTime();
        const bVal = new Date(b.latestTimestamp).getTime();
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      } else if (sortColumn === 'registration_id') {
        const aStr = a.registration_id || '';
        const bStr = b.registration_id || '';
        return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
      } else {
        // Default: sort by point count
        return sortDirection === 'asc' ? a.pointCount - b.pointCount : b.pointCount - a.pointCount;
      }
    });
    
    return sorted;
  }, [groupedDevices, searchTerm, sortColumn, sortDirection, selectedCountries, selectedCarriers, selectedTimezones, selectedForensicFlags, filterByMapBounds, mapBounds, savedDeviceNames]);

  // Paginated data for display
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    
    if (variant === 'sidebar') {
      // For sidebar, use grouped devices
      return filteredAndSortedData.slice(startIndex, endIndex);
    } else {
      // For bottom-panel, use original data points with ALL filters applied
      let filtered = data;
      
      // Apply map bounds filter first
      if (filterByMapBounds && mapBounds) {
        filtered = filtered.filter(device =>
          isWithinBounds(device.latitude, device.longitude, mapBounds)
        );
      }
      
      // Apply search filter to original data
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filtered = filtered.filter(device => {
          const label = getDisplayLabel(device.registration_id);
          return (
            device.registration_id?.toLowerCase().includes(searchLower) ||
            label.toLowerCase().includes(searchLower) ||
            device.latitude?.toString().includes(searchLower) ||
            device.longitude?.toString().includes(searchLower)
          );
        });
      }
      
      // Apply metadata filters (same as grouped view)
      if (selectedCountries.length > 0) {
        filtered = filtered.filter(device => 
          selectedCountries.includes(device.country || '')
        );
      }
      
      if (selectedCarriers.length > 0) {
        filtered = filtered.filter(device =>
          selectedCarriers.includes(device.carrier || '')
        );
      }
      
      if (selectedTimezones.length > 0) {
        filtered = filtered.filter(device =>
          selectedTimezones.includes(device.timezone || '')
        );
      }
      
      if (selectedForensicFlags.length > 0) {
        filtered = filtered.filter(device => {
          // Check all_forensic_flags array first (contains ALL flags from bit decoding)
          if (device.all_forensic_flags && Array.isArray(device.all_forensic_flags)) {
            return device.all_forensic_flags.some(flag => selectedForensicFlags.includes(flag));
          }
          // Fallback: Check individual forensic_flag field
          if (device.forensic_flag && selectedForensicFlags.includes(device.forensic_flag)) {
            return true;
          }
          // Check legacy aggregated forensic_flags
          if ((device as any).forensic_flags?.unique_flags) {
            return (device as any).forensic_flags.unique_flags.some((flag: string) => 
              selectedForensicFlags.includes(flag)
            );
          }
          return false;
        });
      }
      
      // Apply sorting to filtered data
      const sorted = [...filtered].sort((a, b) => {
        let aVal: any = a[sortColumn as keyof GeoNeutronDevice];
        let bVal: any = b[sortColumn as keyof GeoNeutronDevice];
      
      // Handle timestamp sorting
      if (sortColumn === 'timestamp') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      // Handle numeric sorting
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle string sorting
      const aStr = String(aVal || '');
      const bStr = String(bVal || '');
      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
    
      return sorted.slice(startIndex, endIndex);
    }
  }, [filteredAndSortedData, data, searchTerm, sortColumn, sortDirection, page, rowsPerPage, variant, selectedCountries, selectedCarriers, selectedTimezones, selectedForensicFlags, filterByMapBounds, mapBounds]);

  // Simple scroll position preservation using a ref-based approach
  const listScrollRef = useRef<HTMLDivElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const tableScrollTopRef = useRef<number>(0);

  return (
    <Box
      sx={variant === 'sidebar' ? {
        // Sidebar mode - no absolute positioning, full height
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        boxSizing: 'border-box',
      } : {
        // Bottom panel mode - use flexbox in parent instead of absolute positioning
        flexShrink: 0,
        height: isExpanded ? panelHeight : 'auto',
        maxHeight: isExpanded ? panelHeight : 'auto',
        bgcolor: 'background.paper',
        borderTop: 2,
        borderColor: 'primary.main',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Resize Handle - Only show in bottom-panel mode when expanded */}
      {variant === 'bottom-panel' && isExpanded && (
        <Box
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizing(true);
          }}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 6,
            cursor: 'row-resize',
            bgcolor: 'transparent',
            '&:hover': {
              bgcolor: 'primary.light',
            },
            zIndex: 10,
          }}
        />
      )}
      
      {/* Header Bar - Only show in bottom-panel mode or when collapsed in sidebar */}
      {(variant === 'bottom-panel' || !isExpanded) && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'primary.main',
          color: 'white',
          px: 2,
          py: 1,
          cursor: variant === 'bottom-panel' ? 'pointer' : 'default',
          flexShrink: 0,
        }}
        onClick={variant === 'bottom-panel' ? () => setIsExpanded(!isExpanded) : undefined}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {variant === 'bottom-panel' && <TableIcon />}
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {variant === 'bottom-panel' ? 'Data Table ' : ''}{tabs.length > 0 && `(${tabs.reduce((acc, tab) => acc + tab.data.length, 0).toLocaleString()} Points)`}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isLoading && (
            <Typography variant="caption">Loading...</Typography>
          )}
          {!isLoading && tabs.length > 0 && (
            <Chip 
              label={`${tabs.length} search${tabs.length !== 1 ? 'es' : ''}`} 
              size="small" 
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 600
              }} 
            />
          )}
          {variant === 'bottom-panel' && (
          <IconButton size="small" sx={{ color: 'white' }}>
            {isExpanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
          )}
        </Box>
      </Box>
      )}

      {/* Collapsible Content - Always shown in sidebar mode */}
      {(variant === 'bottom-panel' && !isExpanded) ? null : (
        <Box sx={variant === 'sidebar'
        ? { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', width: '100%', maxWidth: '100%' }
        : { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }
      }>
        {/* Tabs */}
        {tabs.length > 0 && (
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'divider', 
            bgcolor: 'background.paper', 
            width: '100%', 
            maxWidth: '100%', 
            overflow: 'hidden',
          }}>
            <Tabs
              value={activeTabIndex}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                '& .MuiTab-root': {
                  minWidth: 120,
                  textTransform: 'none',
                  opacity: 0.6,
                  transition: 'opacity 0.2s ease, border-color 0.2s ease', // More specific transitions
                  borderBottom: '3px solid transparent',
                  '&.Mui-selected': {
                    opacity: 1,
                    bgcolor: 'rgba(25, 118, 210, 0.15)',
                    borderBottom: '3px solid #1976d2',
                    fontWeight: 'bold',
                  },
                  '&:hover': {
                    opacity: 0.9,
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                  },
                },
                '& .MuiTabs-indicator': {
                  display: 'none', // We use custom border instead
                },
                '& .MuiTabs-scroller': {
                  overflowX: 'auto !important',
                  overflowY: 'hidden',
                }
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={tab.id}
                  sx={{ maxWidth: 280, minWidth: 'auto', px: 1.5 }}
                  label={
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.75, 
                      maxWidth: 250, // Fixed max width to ensure X button always visible
                      overflow: 'hidden',
                      width: '100%',
                    }}>
                      <Tooltip title={tab.label} placement="top" arrow>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            maxWidth: 150,
                            minWidth: 40,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flexShrink: 1,
                          }}
                        >
                          {tab.label}
                        </Typography>
                      </Tooltip>
                      {onTabClose && (
                        <Box
                          component="span"
                          onClick={(e) => {
                            e.stopPropagation();
                            onTabClose(tab.id);
                            // Adjust active tab if needed
                            if (!onTabChange) {
                              if (activeTabIndex === index && index > 0) {
                                setInternalActiveTabIndex(index - 1);
                              } else if (activeTabIndex > index) {
                                setInternalActiveTabIndex(activeTabIndex - 1);
                              }
                            }
                          }}
                          sx={{
                            ml: 0.25,
                            p: 0.25,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            flexShrink: 0,
                            minWidth: 20,
                            minHeight: 20,
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </Box>
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Note: Timeline view for PoL has been moved to the main Story Mode tab */}

        <Box sx={{ 
          flex: 1,
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden',
          maxWidth: '100%',
          minHeight: 0
        }}>
          {/* Data Table View for all tabs (Timeline moved to main Story Mode tab) */}
          {/* Search Bar and Export */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by Registration ID, Lat, or Lon..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: '100%',
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.default',
                }
              }}
            />
            {/* Filter Button */}
            <Badge badgeContent={activeFilterCount} color="error">
              <IconButton
                color="primary"
                onClick={() => setFilterDialogOpen(true)}
                disabled={!currentTab || data.length === 0}
                sx={{
                  bgcolor: activeFilterCount > 0 ? 'primary.main' : 'action.hover',
                  color: activeFilterCount > 0 ? 'white' : 'text.secondary',
                  '&:hover': {
                    bgcolor: activeFilterCount > 0 ? 'primary.dark' : 'action.selected',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'action.disabledBackground',
                  },
                }}
                title="Filter by Country, Carrier, Timezone, or Forensic Flags"
              >
                <FilterListIcon />
              </IconButton>
            </Badge>
            {/* Filter by Map View Toggle */}
            {onFilterByMapBoundsChange && (
              <Tooltip title={filterByMapBounds ? "Filter by Map View: ON - Showing only data in visible map area" : "Filter by Map View: OFF - Showing all data"}>
                <IconButton
                  color="primary"
                  onClick={() => onFilterByMapBoundsChange(!filterByMapBounds)}
                  sx={{
                    bgcolor: filterByMapBounds ? 'info.main' : 'action.hover',
                    color: filterByMapBounds ? 'white' : 'text.secondary',
                    '&:hover': {
                      bgcolor: filterByMapBounds ? 'info.dark' : 'info.main',
                      color: 'white',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'action.disabledBackground',
                    },
                  }}
                >
                  <CountryIcon />
                </IconButton>
              </Tooltip>
            )}
            {/* Column Visibility Button - Only for bottom-panel mode with standard columns */}
            {variant === 'bottom-panel' && currentTab?.type !== 'csv' && (
              <IconButton
                color="primary"
                onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
                disabled={!currentTab || data.length === 0}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'action.disabledBackground',
                  },
                }}
                title="Show/Hide Columns"
              >
                <ViewColumnIcon />
              </IconButton>
            )}
            {/* Load All PoL Button - Only show for area search tabs */}
            {currentTab?.type === 'area' && onLoadAllDevices && (
              <Tooltip title="Load Pattern of Life for all devices in this area search">
                <IconButton
                  color="primary"
                  onClick={() => {
                    // Get unique registration IDs from the current tab
                    const uniqueIds = Array.from(new Set(data.map(d => d.registration_id)));
                    onLoadAllDevices(uniqueIds);
                  }}
                  disabled={!currentTab || data.length === 0 || isLoading}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'action.disabledBackground',
                    },
                  }}
                >
                  <LoadAllIcon />
                </IconButton>
              </Tooltip>
            )}
            {/* Export CSV Button */}
            <IconButton
              color="primary"
              onClick={handleExportCSV}
              disabled={!currentTab || filteredAndSortedData.length === 0}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&.Mui-disabled': {
                  bgcolor: 'action.disabledBackground',
                },
              }}
              title="Export to CSV"
            >
              <DownloadIcon />
            </IconButton>
            {/* Clear All Button - Only for Uploaded Data tab */}
            {currentTab?.type === 'csv' && onClearAllUploadedData && (
              <Tooltip title="Clear all uploaded data" placement="top" disableInteractive>
                <IconButton
                  color="error"
                  disableRipple
                  onClick={() => {
                    if (window.confirm('Are you sure you want to remove all uploaded/searched data?')) {
                      onClearAllUploadedData();
                    }
                  }}
                  disabled={!currentTab || filteredAndSortedData.length === 0}
                  sx={{
                    bgcolor: 'error.main',
                    color: 'white',
                    ml: 1,
                    transition: 'none',
                    '&:hover': {
                      bgcolor: 'error.dark',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'action.disabledBackground',
                    },
                  }}
                >
                  <ClearAllIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ px: 2, pb: 1, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
            {searchTerm && (
              <Typography variant="caption" color="text.secondary">
                Showing {filteredAndSortedData.length} of {data.length} results
              </Typography>
            )}
          </Box>

          {/* Top Pagination Controls */}
          {tabs.length > 0 && data.length > 0 && (
            <TablePagination
              component="div"
              count={variant === 'sidebar' ? filteredAndSortedData.length : data.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={variant === 'sidebar' ? [10, 25, 50, 100] : [50, 100, 250, 500]}
              labelRowsPerPage={variant === 'sidebar' ? 'Per page:' : 'Rows per page:'}
              labelDisplayedRows={({ from, to, count }) => 
                variant === 'sidebar' 
                  ? `${from}–${to} of ${count !== -1 ? count : `more than ${to}`} devices`
                  : `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
              sx={{
                borderTop: 1,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                '& .MuiToolbar-root': {
                  paddingLeft: variant === 'sidebar' ? 1 : 2,
                  paddingRight: variant === 'sidebar' ? 1 : 2,
                  minHeight: variant === 'sidebar' ? 48 : 52,
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: variant === 'sidebar' ? '0.75rem' : '0.875rem',
                }
              }}
            />
          )}

          {/* Table or Card List based on variant */}
          {variant === 'sidebar' ? (
            /* Card List View for Sidebar - use position:relative wrapper with absolute scroll area */
            <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
              <Box
                ref={listScrollRef}
                sx={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  overflow: 'auto', 
                  overflowX: 'hidden', 
                  overflowAnchor: 'none',
                  px: 2, 
                  boxSizing: 'border-box',
                }}>
              {isLoading ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">Loading data...</Typography>
                </Box>
              ) : error ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="error">
                    ❌ Error: {error}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Check backend logs or VPN connection
                  </Typography>
                </Box>
              ) : tabs.length === 0 || data.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No data available. Perform an area search to populate this table.
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                  {paginatedData.map((deviceGroup, index) => (
                    <React.Fragment key={`${deviceGroup.registration_id}-${index}`}>
                      <Card 
                        sx={{ 
                          mb: 1.5,
                          cursor: onDeviceClick ? 'pointer' : 'default',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          opacity: hiddenDevices.has(deviceGroup.registration_id) ? 0.5 : 1,
                          filter: hiddenDevices.has(deviceGroup.registration_id) ? 'grayscale(50%)' : 'none',
                        }}
                        onClick={() => onDeviceClick && onDeviceClick(deviceGroup.lastPoint)}
                      >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                          {/* Device ID and Point Count */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, flex: 1, minWidth: 0, overflow: 'hidden', maxWidth: '100%' }}>
                              <Tooltip title={savedDeviceIds.has(deviceGroup.registration_id) ? "⭐ Saved Device" : "Not Saved"} arrow>
                                {savedDeviceIds.has(deviceGroup.registration_id) ? (
                                  <StarIcon sx={{ 
                                    fontSize: 18, 
                                    color: '#FFD700',
                                    flexShrink: 0,
                                    filter: 'drop-shadow(0 0 2px rgba(255, 215, 0, 0.5))'
                                  }} />
                                ) : (
                                  <DeviceIcon sx={{ 
                                    fontSize: 16, 
                                    color: 'primary.main', 
                                    flexShrink: 0 
                                  }} />
                                )}
                              </Tooltip>
                              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontSize: '0.75rem',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontWeight: savedDeviceIds.has(deviceGroup.registration_id) ? 600 : 400,
                                    lineHeight: 1.2,
                                  }}
                                  title={
                                    getDisplayLabel(deviceGroup.registration_id) === deviceGroup.registration_id
                                      ? deviceGroup.registration_id
                                      : `${getDisplayLabel(deviceGroup.registration_id)}\n${deviceGroup.registration_id}`
                                  }
                                >
                                  {getDisplayLabel(deviceGroup.registration_id)}
                                </Typography>
                                {savedDeviceNames.get(deviceGroup.registration_id) &&
                                  getDisplayLabel(deviceGroup.registration_id) !== deviceGroup.registration_id && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      fontFamily: 'monospace',
                                      fontSize: '0.65rem',
                                      display: 'block',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {deviceGroup.registration_id}
                                  </Typography>
                                )}
                              </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                              {/* Country Badge */}
                              {deviceGroup.lastPoint.country && (
                                <Chip 
                                  icon={<CountryIcon sx={{ fontSize: 12 }} />}
                                  label={deviceGroup.lastPoint.country}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { ml: 0.5 } }}
                                />
                              )}
                              <Chip 
                                label={`${deviceGroup.pointCount} pts`}
                                size="small"
                                color="primary"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </Box>
                          </Box>

                          {/* Location */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                            <LocationIcon sx={{ fontSize: 14, color: 'text.secondary', mt: 0.3, flexShrink: 0 }} />
                            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', maxWidth: '100%' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                                Lat: {formatCoordinate(deviceGroup.lastPoint.latitude)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                                Lon: {formatCoordinate(deviceGroup.lastPoint.longitude)}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Latest Timestamp */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                            <TimeIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                              {formatTimestamp(deviceGroup.latestTimestamp)}
                            </Typography>
                          </Box>

                          {/* Source Badge and Action Buttons */}
                          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                            <Chip 
                              label="Tracer API"
                              size="small"
                              sx={{ 
                                height: 18, 
                                fontSize: '0.65rem',
                                bgcolor: 'success.main',
                                color: 'white',
                                '& .MuiChip-label': { px: 0.75 }
                              }}
                            />
                            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                              {/* Action Buttons - using native title for stable tooltips, disableRipple for stability */}
                              <IconButton
                                size="small"
                                title="Zoom to device extent"
                                disableRipple
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onZoomToDeviceExtent) {
                                    const devicePoints = data.filter(d => d.registration_id === deviceGroup.registration_id);
                                    onZoomToDeviceExtent(deviceGroup.registration_id, devicePoints);
                                  }
                                }}
                                sx={{ 
                                  bgcolor: 'secondary.main',
                                  color: 'white',
                                  width: 28,
                                  height: 28,
                                  transition: 'none',
                                  '&:hover': { bgcolor: 'secondary.dark' }
                                }}
                              >
                                <ZoomExtentIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                title={hiddenDevices.has(deviceGroup.registration_id) ? "Show on map" : "Hide from map"}
                                disableRipple
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onToggleDeviceVisibility && onToggleDeviceVisibility(deviceGroup.registration_id);
                                }}
                                sx={{ 
                                  bgcolor: hiddenDevices.has(deviceGroup.registration_id) ? 'action.disabled' : 'primary.main',
                                  color: 'white',
                                  width: 28,
                                  height: 28,
                                  transition: 'none',
                                  '&:hover': { bgcolor: hiddenDevices.has(deviceGroup.registration_id) ? 'action.hover' : 'primary.dark' }
                                }}
                              >
                                {hiddenDevices.has(deviceGroup.registration_id) ? (
                                  <VisibilityOff sx={{ fontSize: 16 }} />
                                ) : (
                                  <Visibility sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                title="Focus: Show only this device on map"
                                disableRipple
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onFocusDevice) {
                                    // Get all device IDs from current data
                                    const allDeviceIds = Array.from(new Set(data.map(d => d.registration_id)));
                                    onFocusDevice(deviceGroup.registration_id, allDeviceIds);
                                  }
                                }}
                                sx={{ 
                                  bgcolor: 'warning.main',
                                  color: 'white',
                                  width: 28,
                                  height: 28,
                                  transition: 'none',
                                  '&:hover': { bgcolor: 'warning.dark' }
                                }}
                              >
                                <FocusIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              {/* Hide Load PoL, Notes, and Save buttons for uploaded/CSV data */}
                              {currentTab?.type !== 'csv' && (
                                <>
                                  <IconButton
                                    size="small"
                                    title="Load Pattern of Life"
                                    disableRipple
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onLoadDevice && onLoadDevice(deviceGroup.registration_id);
                                    }}
                                    sx={{ 
                                      bgcolor: 'primary.main',
                                      color: 'white',
                                      width: 28,
                                      height: 28,
                                      transition: 'none',
                                      '&:hover': { bgcolor: 'primary.dark' }
                                    }}
                                  >
                                    <LoadIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    title="Device Notes"
                                    disableRipple
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDeviceNotes && onDeviceNotes(deviceGroup.registration_id);
                                    }}
                                    sx={{ 
                                      bgcolor: 'secondary.main',
                                      color: 'white',
                                      width: 28,
                                      height: 28,
                                      mr: 1,
                                      transition: 'none',
                                      '&:hover': { bgcolor: 'secondary.dark' }
                                    }}
                                  >
                                    <NoteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    title="Save to Watchlist"
                                    disableRipple
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onSaveDevice && onSaveDevice(deviceGroup.registration_id);
                                    }}
                                    sx={{ 
                                      bgcolor: 'secondary.main',
                                      color: 'white',
                                      width: 28,
                                      height: 28,
                                      transition: 'none',
                                      '&:hover': { bgcolor: 'secondary.dark' }
                                    }}
                                  >
                                    <StarBorderIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </>
                              )}
                              {/* Delete button for uploaded/CSV data */}
                              {currentTab?.type === 'csv' && onDeleteUploadedPoint && (
                                <IconButton
                                  size="small"
                                  title="Remove this point"
                                  disableRipple
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Find the point ID from the device group data
                                    const pointId = (deviceGroup as any).id || (deviceGroup as any).point_id || deviceGroup.registration_id;
                                    onDeleteUploadedPoint(pointId);
                                  }}
                                  sx={{ 
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    width: 28,
                                    height: 28,
                                    transition: 'none',
                                    '&:hover': { bgcolor: 'error.dark' }
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                          
                          {/* Forensic Flags Display */}
                          {(() => {
                            // Get all flags from allForensicFlags Set or legacy forensic_flags object
                            const allFlags = deviceGroup.allForensicFlags 
                              ? Array.from(deviceGroup.allForensicFlags)
                              : deviceGroup.forensic_flags?.unique_flags || [];
                            
                            if (allFlags.length === 0) return null;
                            
                            return (
                              <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                                <Tooltip 
                                  title={
                                    <Box>
                                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                        All Forensic Flags:
                                      </Typography>
                                      {allFlags.map((flag: string, idx: number) => (
                                        <Typography key={idx} variant="caption" sx={{ display: 'block', mt: 0.3 }}>
                                          • {flag}
                                        </Typography>
                                      ))}
                                    </Box>
                                  }
                                  arrow
                                  placement="right"
                                  enterDelay={200}
                                  leaveDelay={0}
                                >
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary" 
                                    sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      cursor: 'help',
                                      '&:hover': { color: 'primary.main' }
                                    }}
                                  >
                                    <strong>🚩 Forensic Flags:</strong>&nbsp;{allFlags.length} flag{allFlags.length !== 1 ? 's' : ''}
                                  </Typography>
                                </Tooltip>
                              </Box>
                            );
                          })()}
                        </CardContent>
                      </Card>
                    </React.Fragment>
                  ))}
                </List>
              )}
              </Box>
            </Box>
          ) : (
            /* Table View for Bottom Panel */
          <TableContainer
            ref={tableScrollRef}
            sx={{ flex: 1, overflow: 'auto', overflowAnchor: 'none' }}
            onScroll={(e) => {
              tableScrollTopRef.current = (e.target as HTMLDivElement).scrollTop;
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {currentTab?.type === 'csv' && currentTab.csvColumns ? (
                    // Dynamic columns for CSV
                    currentTab.csvColumns.map((column) => (
                      <TableCell key={column} sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>
                        <TableSortLabel
                          active={sortColumn === column}
                          direction={sortColumn === column ? sortDirection : 'asc'}
                          onClick={() => handleSort(column)}
                        >
                          {column.charAt(0).toUpperCase() + column.slice(1).replace(/_/g, ' ')}
                        </TableSortLabel>
                      </TableCell>
                    ))
                  ) : (
                    // Standard columns for non-CSV tabs with resizable support
                    <>
                      {columnVisibility.source !== false && (
                        <TableCell 
                          sx={{ 
                            fontWeight: 'bold', 
                            bgcolor: 'background.paper',
                            width: columnWidths.source || defaultColumnWidths.source,
                            minWidth: columnWidths.source || defaultColumnWidths.source,
                            maxWidth: columnWidths.source || defaultColumnWidths.source,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          Source
                          <Box
                            onMouseDown={(e) => handleResizeStart('source', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.registration_id !== false && (
                        <TableCell 
                          sx={{ 
                            fontWeight: 'bold', 
                            bgcolor: 'background.paper',
                            width: columnWidths.registration_id || defaultColumnWidths.registration_id,
                            minWidth: columnWidths.registration_id || defaultColumnWidths.registration_id,
                            maxWidth: columnWidths.registration_id || defaultColumnWidths.registration_id,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          <TableSortLabel
                            active={sortColumn === 'registration_id'}
                            direction={sortColumn === 'registration_id' ? sortDirection : 'asc'}
                            onClick={() => handleSort('registration_id')}
                          >
                            Registration ID
                          </TableSortLabel>
                          <Box
                            onMouseDown={(e) => handleResizeStart('registration_id', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.latitude !== false && (
                        <TableCell 
                          sx={{ 
                            fontWeight: 'bold', 
                            bgcolor: 'background.paper',
                            width: columnWidths.latitude || defaultColumnWidths.latitude,
                            minWidth: columnWidths.latitude || defaultColumnWidths.latitude,
                            maxWidth: columnWidths.latitude || defaultColumnWidths.latitude,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          <TableSortLabel
                            active={sortColumn === 'latitude'}
                            direction={sortColumn === 'latitude' ? sortDirection : 'asc'}
                            onClick={() => handleSort('latitude')}
                          >
                            Latitude
                          </TableSortLabel>
                          <Box
                            onMouseDown={(e) => handleResizeStart('latitude', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.longitude !== false && (
                        <TableCell 
                          sx={{ 
                            fontWeight: 'bold', 
                            bgcolor: 'background.paper',
                            width: columnWidths.longitude || defaultColumnWidths.longitude,
                            minWidth: columnWidths.longitude || defaultColumnWidths.longitude,
                            maxWidth: columnWidths.longitude || defaultColumnWidths.longitude,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          <TableSortLabel
                            active={sortColumn === 'longitude'}
                            direction={sortColumn === 'longitude' ? sortDirection : 'asc'}
                            onClick={() => handleSort('longitude')}
                          >
                            Longitude
                          </TableSortLabel>
                          <Box
                            onMouseDown={(e) => handleResizeStart('longitude', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.timestamp !== false && (
                        <TableCell 
                          sx={{ 
                            fontWeight: 'bold', 
                            bgcolor: 'background.paper',
                            width: columnWidths.timestamp || defaultColumnWidths.timestamp,
                            minWidth: columnWidths.timestamp || defaultColumnWidths.timestamp,
                            maxWidth: columnWidths.timestamp || defaultColumnWidths.timestamp,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          <TableSortLabel
                            active={sortColumn === 'timestamp'}
                            direction={sortColumn === 'timestamp' ? sortDirection : 'asc'}
                            onClick={() => handleSort('timestamp')}
                          >
                            Timestamp
                          </TableSortLabel>
                          <Box
                            onMouseDown={(e) => handleResizeStart('timestamp', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.accuracy !== false && (
                        <TableCell 
                          sx={{ 
                            fontWeight: 'bold', 
                            bgcolor: 'background.paper',
                            width: columnWidths.accuracy || defaultColumnWidths.accuracy,
                            minWidth: columnWidths.accuracy || defaultColumnWidths.accuracy,
                            maxWidth: columnWidths.accuracy || defaultColumnWidths.accuracy,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          <TableSortLabel
                            active={sortColumn === 'accuracy'}
                            direction={sortColumn === 'accuracy' ? sortDirection : 'asc'}
                            onClick={() => handleSort('accuracy')}
                          >
                            Accuracy (m)
                          </TableSortLabel>
                          <Box
                            onMouseDown={(e) => handleResizeStart('accuracy', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.ip_address !== false && (
                        <TableCell 
                          sx={{ 
                            fontWeight: 'bold', 
                            bgcolor: 'background.paper',
                            width: columnWidths.ip_address || defaultColumnWidths.ip_address,
                            minWidth: columnWidths.ip_address || defaultColumnWidths.ip_address,
                            maxWidth: columnWidths.ip_address || defaultColumnWidths.ip_address,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          IP Address
                          <Box
                            onMouseDown={(e) => handleResizeStart('ip_address', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.country !== false && (
                        <TableCell 
                          sx={{ 
                            fontWeight: 'bold', 
                            bgcolor: 'background.paper',
                            width: columnWidths.country || defaultColumnWidths.country,
                            minWidth: columnWidths.country || defaultColumnWidths.country,
                            maxWidth: columnWidths.country || defaultColumnWidths.country,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          Country
                          <Box
                            onMouseDown={(e) => handleResizeStart('country', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.carrier !== false && (
                        <TableCell 
                          sx={{ 
                            fontWeight: 'bold', 
                            bgcolor: 'background.paper',
                            width: columnWidths.carrier || defaultColumnWidths.carrier,
                            minWidth: columnWidths.carrier || defaultColumnWidths.carrier,
                            maxWidth: columnWidths.carrier || defaultColumnWidths.carrier,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          Carrier
                          <Box
                            onMouseDown={(e) => handleResizeStart('carrier', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.timezone !== false && (
                        <TableCell 
                          sx={{ 
                            fontWeight: 'bold', 
                            bgcolor: 'background.paper',
                            width: columnWidths.timezone || defaultColumnWidths.timezone,
                            minWidth: columnWidths.timezone || defaultColumnWidths.timezone,
                            maxWidth: columnWidths.timezone || defaultColumnWidths.timezone,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          Timezone
                          <Box
                            onMouseDown={(e) => handleResizeStart('timezone', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.browser_family !== false && (
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            bgcolor: 'background.paper',
                            width: columnWidths.browser_family || defaultColumnWidths.browser_family,
                            minWidth: columnWidths.browser_family || defaultColumnWidths.browser_family,
                            maxWidth: columnWidths.browser_family || defaultColumnWidths.browser_family,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          Browser Family
                          <Box
                            onMouseDown={(e) => handleResizeStart('browser_family', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.browser_version !== false && (
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            bgcolor: 'background.paper',
                            width: columnWidths.browser_version || defaultColumnWidths.browser_version,
                            minWidth: columnWidths.browser_version || defaultColumnWidths.browser_version,
                            maxWidth: columnWidths.browser_version || defaultColumnWidths.browser_version,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          Browser Version
                          <Box
                            onMouseDown={(e) => handleResizeStart('browser_version', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.os_family !== false && (
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            bgcolor: 'background.paper',
                            width: columnWidths.os_family || defaultColumnWidths.os_family,
                            minWidth: columnWidths.os_family || defaultColumnWidths.os_family,
                            maxWidth: columnWidths.os_family || defaultColumnWidths.os_family,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          OS Family
                          <Box
                            onMouseDown={(e) => handleResizeStart('os_family', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.os_version !== false && (
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            bgcolor: 'background.paper',
                            width: columnWidths.os_version || defaultColumnWidths.os_version,
                            minWidth: columnWidths.os_version || defaultColumnWidths.os_version,
                            maxWidth: columnWidths.os_version || defaultColumnWidths.os_version,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          OS Version
                          <Box
                            onMouseDown={(e) => handleResizeStart('os_version', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.device_brand !== false && (
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            bgcolor: 'background.paper',
                            width: columnWidths.device_brand || defaultColumnWidths.device_brand,
                            minWidth: columnWidths.device_brand || defaultColumnWidths.device_brand,
                            maxWidth: columnWidths.device_brand || defaultColumnWidths.device_brand,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          Device Brand
                          <Box
                            onMouseDown={(e) => handleResizeStart('device_brand', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.device_family !== false && (
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            bgcolor: 'background.paper',
                            width: columnWidths.device_family || defaultColumnWidths.device_family,
                            minWidth: columnWidths.device_family || defaultColumnWidths.device_family,
                            maxWidth: columnWidths.device_family || defaultColumnWidths.device_family,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          Device Family
                          <Box
                            onMouseDown={(e) => handleResizeStart('device_family', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.device_model !== false && (
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            bgcolor: 'background.paper',
                            width: columnWidths.device_model || defaultColumnWidths.device_model,
                            minWidth: columnWidths.device_model || defaultColumnWidths.device_model,
                            maxWidth: columnWidths.device_model || defaultColumnWidths.device_model,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          Device Model
                          <Box
                            onMouseDown={(e) => handleResizeStart('device_model', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.advertiser_eu !== false && (
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            bgcolor: 'background.paper',
                            width: columnWidths.advertiser_eu || defaultColumnWidths.advertiser_eu,
                            minWidth: columnWidths.advertiser_eu || defaultColumnWidths.advertiser_eu,
                            maxWidth: columnWidths.advertiser_eu || defaultColumnWidths.advertiser_eu,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          Advertiser EU
                          <Box
                            onMouseDown={(e) => handleResizeStart('advertiser_eu', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.created_on !== false && (
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            bgcolor: 'background.paper',
                            width: columnWidths.created_on || defaultColumnWidths.created_on,
                            minWidth: columnWidths.created_on || defaultColumnWidths.created_on,
                            maxWidth: columnWidths.created_on || defaultColumnWidths.created_on,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          Created On
                          <Box
                            onMouseDown={(e) => handleResizeStart('created_on', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.updated_on !== false && (
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            bgcolor: 'background.paper',
                            width: columnWidths.updated_on || defaultColumnWidths.updated_on,
                            minWidth: columnWidths.updated_on || defaultColumnWidths.updated_on,
                            maxWidth: columnWidths.updated_on || defaultColumnWidths.updated_on,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          Updated On
                          <Box
                            onMouseDown={(e) => handleResizeStart('updated_on', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.user_agent_full !== false && (
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            bgcolor: 'background.paper',
                            width: columnWidths.user_agent_full || defaultColumnWidths.user_agent_full,
                            minWidth: columnWidths.user_agent_full || defaultColumnWidths.user_agent_full,
                            maxWidth: columnWidths.user_agent_full || defaultColumnWidths.user_agent_full,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          User Agent
                          <Box
                            onMouseDown={(e) => handleResizeStart('user_agent_full', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.forensic_flag !== false && (
                        <TableCell 
                          sx={{ 
                            fontWeight: 'bold', 
                            bgcolor: 'background.paper',
                            width: columnWidths.forensic_flag || defaultColumnWidths.forensic_flag,
                            minWidth: columnWidths.forensic_flag || defaultColumnWidths.forensic_flag,
                            maxWidth: columnWidths.forensic_flag || defaultColumnWidths.forensic_flag,
                            position: 'relative',
                            paddingRight: '4px',
                          }}
                        >
                          Forensic Flag
                          <Box
                            onMouseDown={(e) => handleResizeStart('forensic_flag', e)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              cursor: 'col-resize',
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            }}
                          />
                        </TableCell>
                      )}
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell 
                      colSpan={
                        currentTab?.type === 'csv' && currentTab.csvColumns 
                          ? currentTab.csvColumns.length 
                          : Object.values(columnVisibility).filter(v => v !== false).length
                      } 
                      align="center" 
                      sx={{ py: 4 }}
                    >
                      <Typography color="text.secondary">Loading data...</Typography>
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell 
                      colSpan={
                        currentTab?.type === 'csv' && currentTab.csvColumns 
                          ? currentTab.csvColumns.length 
                          : Object.values(columnVisibility).filter(v => v !== false).length
                      } 
                      align="center" 
                      sx={{ py: 4 }}
                    >
                      <Typography color="error">
                        ❌ Error: {error}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Check backend logs or VPN connection
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : tabs.length === 0 || data.length === 0 ? (
                  <TableRow>
                    <TableCell 
                      colSpan={
                        currentTab?.type === 'csv' && currentTab.csvColumns 
                          ? currentTab.csvColumns.length 
                          : Object.values(columnVisibility).filter(v => v !== false).length
                      } 
                      align="center" 
                      sx={{ py: 4 }}
                    >
                      <Typography color="text.secondary">
                        No data available. {currentTab?.type === 'csv' ? 'Upload a CSV file' : 'Perform an area search'} to populate this table.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((deviceData, index) => {
                    // Type assertion for bottom-panel mode (uses original GeoNeutronDevice objects)
                    const device = deviceData as GeoNeutronDevice;

                    return (
                    <TableRow
                      key={`${device.registration_id || index}-${index}`}
                      hover
                      sx={{
                        cursor: onDeviceClick ? 'pointer' : 'default',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                      onClick={() => onDeviceClick && onDeviceClick(device)}
                    >
                      {currentTab?.type === 'csv' && currentTab.csvColumns ? (
                        // Dynamic cells for CSV
                        currentTab.csvColumns.map((column) => (
                          <TableCell key={column}>
                            <Typography variant="body2" sx={{ fontFamily: column === 'latitude' || column === 'longitude' ? 'monospace' : 'inherit' }}>
                              {column === 'latitude' || column === 'longitude' 
                                  ? formatCoordinate((device as any)[column])
                                : column === 'timestamp'
                                  ? formatTimestamp((device as any)[column])
                                  : column === 'accuracy' && (device as any)[column] !== undefined && (device as any)[column] !== null
                                  ? Math.round(Number((device as any)[column]))
                                  : (device as any)[column] || '-'}
                            </Typography>
                          </TableCell>
                        ))
                      ) : (
                        // Standard cells for non-CSV tabs with column width/visibility support
                        <>
                          {columnVisibility.source !== false && (
                            <TableCell sx={{ width: columnWidths.source || defaultColumnWidths.source, minWidth: columnWidths.source || defaultColumnWidths.source, maxWidth: columnWidths.source || defaultColumnWidths.source }}>
                              <Chip 
                                  label="Tracer API" 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            </TableCell>
                          )}
                          {columnVisibility.registration_id !== false && (
                            <TableCell sx={{ width: columnWidths.registration_id || defaultColumnWidths.registration_id, minWidth: columnWidths.registration_id || defaultColumnWidths.registration_id, maxWidth: columnWidths.registration_id || defaultColumnWidths.registration_id }}>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: '0.8rem', lineHeight: 1.2 }}
                                  title={
                                    getDisplayLabel(device.registration_id) === device.registration_id
                                      ? device.registration_id
                                      : `${getDisplayLabel(device.registration_id)}\n${device.registration_id}`
                                  }
                                >
                                  {getDisplayLabel(device.registration_id)}
                                </Typography>
                                {getDisplayLabel(device.registration_id) !== device.registration_id && (
                                  <Typography
                                    variant="caption"
                                    component="div"
                                    sx={{
                                      fontFamily: 'monospace',
                                      fontSize: '0.65rem',
                                      color: 'text.secondary',
                                      mt: 0.25,
                                    }}
                                  >
                                    {device.registration_id}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          )}
                          {columnVisibility.latitude !== false && (
                            <TableCell sx={{ width: columnWidths.latitude || defaultColumnWidths.latitude, minWidth: columnWidths.latitude || defaultColumnWidths.latitude, maxWidth: columnWidths.latitude || defaultColumnWidths.latitude }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {formatCoordinate(device.latitude)}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.longitude !== false && (
                            <TableCell sx={{ width: columnWidths.longitude || defaultColumnWidths.longitude, minWidth: columnWidths.longitude || defaultColumnWidths.longitude, maxWidth: columnWidths.longitude || defaultColumnWidths.longitude }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {formatCoordinate(device.longitude)}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.timestamp !== false && (
                            <TableCell sx={{ width: columnWidths.timestamp || defaultColumnWidths.timestamp, minWidth: columnWidths.timestamp || defaultColumnWidths.timestamp, maxWidth: columnWidths.timestamp || defaultColumnWidths.timestamp }}>
                              <Typography variant="body2">
                                {formatTimestamp(device.timestamp)}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.accuracy !== false && (
                            <TableCell sx={{ width: columnWidths.accuracy || defaultColumnWidths.accuracy, minWidth: columnWidths.accuracy || defaultColumnWidths.accuracy, maxWidth: columnWidths.accuracy || defaultColumnWidths.accuracy }}>
                              <Typography variant="body2">
                                {device.accuracy ? Math.round(device.accuracy) : '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.ip_address !== false && (
                            <TableCell sx={{ width: columnWidths.ip_address || defaultColumnWidths.ip_address, minWidth: columnWidths.ip_address || defaultColumnWidths.ip_address, maxWidth: columnWidths.ip_address || defaultColumnWidths.ip_address }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {device.ip_address || '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.country !== false && (
                            <TableCell sx={{ width: columnWidths.country || defaultColumnWidths.country, minWidth: columnWidths.country || defaultColumnWidths.country, maxWidth: columnWidths.country || defaultColumnWidths.country }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {device.country || '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.carrier !== false && (
                            <TableCell sx={{ width: columnWidths.carrier || defaultColumnWidths.carrier, minWidth: columnWidths.carrier || defaultColumnWidths.carrier, maxWidth: columnWidths.carrier || defaultColumnWidths.carrier }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {device.carrier || '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.timezone !== false && (
                            <TableCell sx={{ width: columnWidths.timezone || defaultColumnWidths.timezone, minWidth: columnWidths.timezone || defaultColumnWidths.timezone, maxWidth: columnWidths.timezone || defaultColumnWidths.timezone }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {device.timezone || '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.browser_family !== false && (
                            <TableCell sx={{ width: columnWidths.browser_family || defaultColumnWidths.browser_family, minWidth: columnWidths.browser_family || defaultColumnWidths.browser_family, maxWidth: columnWidths.browser_family || defaultColumnWidths.browser_family }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {device.browser_family || '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.browser_version !== false && (
                            <TableCell sx={{ width: columnWidths.browser_version || defaultColumnWidths.browser_version, minWidth: columnWidths.browser_version || defaultColumnWidths.browser_version, maxWidth: columnWidths.browser_version || defaultColumnWidths.browser_version }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {device.browser_version || '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.os_family !== false && (
                            <TableCell sx={{ width: columnWidths.os_family || defaultColumnWidths.os_family, minWidth: columnWidths.os_family || defaultColumnWidths.os_family, maxWidth: columnWidths.os_family || defaultColumnWidths.os_family }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {device.os_family || '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.os_version !== false && (
                            <TableCell sx={{ width: columnWidths.os_version || defaultColumnWidths.os_version, minWidth: columnWidths.os_version || defaultColumnWidths.os_version, maxWidth: columnWidths.os_version || defaultColumnWidths.os_version }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {device.os_version || '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.device_brand !== false && (
                            <TableCell sx={{ width: columnWidths.device_brand || defaultColumnWidths.device_brand, minWidth: columnWidths.device_brand || defaultColumnWidths.device_brand, maxWidth: columnWidths.device_brand || defaultColumnWidths.device_brand }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {device.device_brand || '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.device_family !== false && (
                            <TableCell sx={{ width: columnWidths.device_family || defaultColumnWidths.device_family, minWidth: columnWidths.device_family || defaultColumnWidths.device_family, maxWidth: columnWidths.device_family || defaultColumnWidths.device_family }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {device.device_family || '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.device_model !== false && (
                            <TableCell sx={{ width: columnWidths.device_model || defaultColumnWidths.device_model, minWidth: columnWidths.device_model || defaultColumnWidths.device_model, maxWidth: columnWidths.device_model || defaultColumnWidths.device_model }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {device.device_model || '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.advertiser_eu !== false && (
                            <TableCell sx={{ width: columnWidths.advertiser_eu || defaultColumnWidths.advertiser_eu, minWidth: columnWidths.advertiser_eu || defaultColumnWidths.advertiser_eu, maxWidth: columnWidths.advertiser_eu || defaultColumnWidths.advertiser_eu }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {device.advertiser_eu === true ? 'Yes' : device.advertiser_eu === false ? 'No' : '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.created_on !== false && (
                            <TableCell sx={{ width: columnWidths.created_on || defaultColumnWidths.created_on, minWidth: columnWidths.created_on || defaultColumnWidths.created_on, maxWidth: columnWidths.created_on || defaultColumnWidths.created_on }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                  {device.created_on ? formatTimestamp(device.created_on) : '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.updated_on !== false && (
                            <TableCell sx={{ width: columnWidths.updated_on || defaultColumnWidths.updated_on, minWidth: columnWidths.updated_on || defaultColumnWidths.updated_on, maxWidth: columnWidths.updated_on || defaultColumnWidths.updated_on }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {device.updated_on ? formatTimestamp(device.updated_on) : '-'}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.user_agent_full !== false && (
                            <TableCell sx={{ width: columnWidths.user_agent_full || defaultColumnWidths.user_agent_full, minWidth: columnWidths.user_agent_full || defaultColumnWidths.user_agent_full, maxWidth: columnWidths.user_agent_full || defaultColumnWidths.user_agent_full }}>
                              <Tooltip title={device.user_agent_full || ''} enterDelay={500}>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                                  {device.user_agent_full || '-'}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          )}
                          {columnVisibility.forensic_flag !== false && (
                            <TableCell sx={{ width: columnWidths.forensic_flag || defaultColumnWidths.forensic_flag, minWidth: columnWidths.forensic_flag || defaultColumnWidths.forensic_flag, maxWidth: columnWidths.forensic_flag || defaultColumnWidths.forensic_flag }}>
                              {(() => {
                                // Get all flags for this device (prefer all_forensic_flags array)
                                const allFlags = device.all_forensic_flags && device.all_forensic_flags.length > 0 
                                  ? device.all_forensic_flags 
                                  : device.forensic_flag 
                                    ? [device.forensic_flag] 
                                    : [];
                                
                                if (allFlags.length === 0) {
                                  return <Typography variant="body2" color="text.secondary">-</Typography>;
                                }
                                
                                // Show primary flag + count if there are more
                                const primaryFlag = device.forensic_flag || allFlags[0];
                                const hasMultiple = allFlags.length > 1;
                                
                                return (
                                  <Tooltip 
                                    title={
                                      hasMultiple ? (
                                        <Box>
                                          <Typography variant="caption" sx={{ fontWeight: 600 }}>All Flags:</Typography>
                                          {allFlags.map((flag, idx) => (
                                            <Typography key={idx} variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                              • {flag}
                                            </Typography>
                                          ))}
                                        </Box>
                                      ) : primaryFlag
                                    }
                                    arrow
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Chip
                                        label={primaryFlag}
                                        size="small"
                                        color={
                                          // Quality issues (warning emoji flags) - red
                                          primaryFlag.includes('⚠️') ? 'error' :
                                          // High accuracy - green
                                          primaryFlag.includes('High Accuracy') ? 'success' :
                                          // Moderate accuracy - warning yellow
                                          primaryFlag.includes('Moderate') ? 'warning' :
                                          // Low accuracy - orange/error
                                          primaryFlag.includes('Low') ? 'error' :
                                          // Source flags - info blue
                                          primaryFlag.includes('Cell') || 
                                          primaryFlag.includes('IP') || 
                                          primaryFlag.includes('Mobile') ? 'info' :
                                          // Behavioral flags - default
                                          primaryFlag.includes('Driving') || 
                                          primaryFlag.includes('Recurring') ? 'default' :
                                          // Regional flags - default
                                          primaryFlag.includes('US') || 
                                          primaryFlag.includes('EU') ? 'default' : 'default'
                                        }
                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                      />
                                      {hasMultiple && (
                                        <Chip
                                          label={`+${allFlags.length - 1}`}
                                          size="small"
                                          variant="outlined"
                                          sx={{ 
                                            height: 20, 
                                            fontSize: '0.65rem',
                                            minWidth: '32px',
                                            '& .MuiChip-label': { px: 0.5 }
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Tooltip>
                                );
                              })()}
                            </TableCell>
                          )}
                        </>
                      )}
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          
          {/* Bottom Pagination Controls */}
          <TablePagination
            component="div"
            count={variant === 'sidebar' ? filteredAndSortedData.length : data.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={variant === 'sidebar' ? [10, 25, 50, 100] : [50, 100, 250, 500]}
            labelRowsPerPage={variant === 'sidebar' ? 'Per page:' : 'Rows per page:'}
            labelDisplayedRows={({ from, to, count }) => 
              variant === 'sidebar' 
                ? `${from}–${to} of ${count !== -1 ? count : `more than ${to}`} devices`
                : `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              '& .MuiToolbar-root': {
                paddingLeft: variant === 'sidebar' ? 1 : 2,
                paddingRight: variant === 'sidebar' ? 1 : 2,
                minHeight: variant === 'sidebar' ? 48 : 52,
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: variant === 'sidebar' ? '0.75rem' : '0.875rem',
              }
            }}
          />
        </Box>
      </Box>
      )}

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon />
            <Typography variant="h6">Filter Devices</Typography>
            {activeFilterCount > 0 && (
              <Chip 
                label={`${activeFilterCount} active`} 
                size="small" 
                color="primary"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          <IconButton edge="end" onClick={() => setFilterDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          {/* Country Filter */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Country ({filterOptions.countries.length} available)
            </Typography>
            {filterOptions.countries.length > 0 ? (
              <FormGroup>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {filterOptions.countries.map(country => (
                    <FormControlLabel
                      key={country}
                      control={
                        <Checkbox
                          checked={selectedCountries.includes(country)}
                          onChange={() => handleToggleCountry(country)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {country}
                        </Typography>
                      }
                      sx={{ m: 0, mr: 2 }}
                    />
                  ))}
                </Box>
              </FormGroup>
            ) : (
              <Typography variant="body2" color="text.secondary">No countries found in data</Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Carrier Filter */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Carrier ({filterOptions.carriers.length} available)
            </Typography>
            {filterOptions.carriers.length > 0 ? (
              <FormGroup>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {filterOptions.carriers.map(carrier => (
                    <FormControlLabel
                      key={carrier}
                      control={
                        <Checkbox
                          checked={selectedCarriers.includes(carrier)}
                          onChange={() => handleToggleCarrier(carrier)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {carrier}
                        </Typography>
                      }
                      sx={{ m: 0, mr: 1, mb: 0.5 }}
                    />
                  ))}
                </Box>
              </FormGroup>
            ) : (
              <Typography variant="body2" color="text.secondary">No carriers found in data</Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Timezone Filter */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Timezone ({filterOptions.timezones.length} available)
            </Typography>
            {filterOptions.timezones.length > 0 ? (
              <FormGroup>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {filterOptions.timezones.map(timezone => (
                    <FormControlLabel
                      key={timezone}
                      control={
                        <Checkbox
                          checked={selectedTimezones.includes(timezone)}
                          onChange={() => handleToggleTimezone(timezone)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {timezone}
                        </Typography>
                      }
                      sx={{ m: 0, mr: 1, mb: 0.5 }}
                    />
                  ))}
                </Box>
              </FormGroup>
            ) : (
              <Typography variant="body2" color="text.secondary">No timezones found in data</Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Forensic Flags Filter */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Forensic Flags ({filterOptions.forensicFlags.length} available)
            </Typography>
            {filterOptions.forensicFlags.length > 0 ? (
              <FormGroup>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {filterOptions.forensicFlags.map(flag => (
                    <FormControlLabel
                      key={flag}
                      control={
                        <Checkbox
                          checked={selectedForensicFlags.includes(flag)}
                          onChange={() => handleToggleForensicFlag(flag)}
                          size="small"
                        />
                      }
                      label={
                        <Chip
                          label={flag}
                          size="small"
                          color={
                            flag.includes('High') || flag.includes('Accurate') ? 'success' :
                            flag.includes('Low') || flag.includes('Stale') ? 'error' :
                            flag.includes('Wi-Fi') || flag.includes('WiFi') ? 'warning' :
                            flag.includes('GPS') ? 'success' :
                            flag.includes('Mobile') ? 'info' : 'default'
                          }
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      }
                      sx={{ m: 0, mr: 1, mb: 0.5 }}
                    />
                  ))}
                </Box>
              </FormGroup>
            ) : (
              <Typography variant="body2" color="text.secondary">No forensic flags found in data</Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClearFilters}
            disabled={activeFilterCount === 0}
            color="warning"
          >
            Clear All
          </Button>
          <Button onClick={() => setFilterDialogOpen(false)} variant="contained">
            Apply Filters ({filteredAndSortedData.length} devices)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Column Visibility Menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={Boolean(columnMenuAnchor)}
        onClose={() => setColumnMenuAnchor(null)}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: 250,
          },
        }}
      >
        <MenuItem disabled>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Show/Hide Columns
          </Typography>
        </MenuItem>
        <Divider />
        {Object.entries(defaultColumnVisibility).map(([key, _]) => {
          const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
          return (
            <MenuItem
              key={key}
              onClick={() => handleToggleColumnVisibility(key)}
              sx={{ py: 0.5 }}
            >
              <Checkbox
                checked={columnVisibility[key] !== false}
                size="small"
                sx={{ mr: 1 }}
              />
              <Typography variant="body2">{label}</Typography>
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem
          onClick={() => {
            setColumnVisibility(defaultColumnVisibility);
            saveColumnVisibility(defaultColumnVisibility);
          }}
        >
          <Typography variant="body2" color="primary">
            Show All Columns
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const resetWidths = defaultColumnWidths;
            setColumnWidths(resetWidths);
            saveColumnWidths(resetWidths);
          }}
        >
          <Typography variant="body2" color="primary">
            Reset Column Widths
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default GeoNeutronDataTable;

